import { useRef, useState } from "react";
import usaMap from "@svg-maps/usa";
import { getServices, getState, tierColor, SERVICE_META } from "@/data/serviceAreas";

const badge = (key) => {
  const m = SERVICE_META[key];
  return (
    <span
      key={key}
      className="rounded-full px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider"
      style={{ backgroundColor: `${m.color}22`, color: m.color }}
    >
      {m.short}
    </span>
  );
};

export default function USMap({ onSelect, className }) {
  const [hover, setHover] = useState(null);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const wrapRef = useRef(null);

  const move = (e) => {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (rect) setPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  const hovered = hover ? getState(hover) : null;
  const hoveredServices = hover ? getServices(hover) : null;

  return (
    <div ref={wrapRef} className={className} onMouseMove={move} data-testid="us-map">
      <svg
        viewBox={usaMap.viewBox}
        role="img"
        aria-label="United States commercial energy service coverage map"
        className="h-auto w-full"
      >
        {usaMap.locations.map((loc) => {
          const supported = !!getServices(loc.id);
          const active = hover === loc.id;
          return (
            <path
              key={loc.id}
              data-testid={`us-map-${loc.id}`}
              d={loc.path}
              fill={tierColor(loc.id, active)}
              stroke={active ? "#ffffff" : "rgba(255,255,255,0.10)"}
              strokeWidth={active ? 1.4 : 0.8}
              style={{
                cursor: supported ? "pointer" : "default",
                transition: "fill 0.2s ease, stroke 0.2s ease",
              }}
              onMouseEnter={() => supported && setHover(loc.id)}
              onMouseLeave={() => setHover(null)}
              onClick={() => supported && onSelect && onSelect(loc.id)}
            />
          );
        })}
      </svg>

      {hovered && hoveredServices && (
        <div
          className="pointer-events-none absolute z-20 w-60 -translate-x-1/2 rounded-sm border border-white/15 bg-navy-dark/95 p-4 shadow-xl backdrop-blur"
          style={{ left: pos.x, top: pos.y + 18 }}
        >
          <p className="font-heading text-sm font-medium text-white">{hovered.name}</p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {hoveredServices.electricity && badge("electricity")}
            {hoveredServices.gas && badge("gas")}
            {hoveredServices.battery && badge("battery")}
          </div>
          {(hovered.electricity?.note || hovered.gas?.note) && (
            <p className="mt-2 font-mono text-[10px] leading-relaxed text-white/50">
              {hovered.gas?.note || hovered.electricity?.note}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export const MapLegend = ({ dark = true }) => {
  const t = dark ? "text-white/70" : "text-slatey";
  const items = [
    { c: "#06B6D4", l: "Electricity, Gas & Battery" },
    { c: "#0052FF", l: "Electricity & Gas" },
    { c: "#00C853", l: "Natural Gas" },
    { c: dark ? "#131e3b" : "#e2e8f0", l: "Not yet available" },
  ];
  return (
    <div className="flex flex-wrap gap-x-6 gap-y-3">
      {items.map((i) => (
        <div key={i.l} className={`flex items-center gap-2 text-xs ${t}`}>
          <span className="h-3 w-3 rounded-sm" style={{ backgroundColor: i.c }} />
          {i.l}
        </div>
      ))}
    </div>
  );
};
